import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import { Empty } from 'antd';
import moment from 'moment';
import { IAnalyzeChannelsChannelShareModel } from 'entities/AnalyzeChannels/AnalyzeChannels.models';
import {
  CHANNEL_ORDER,
  getChannelColor,
  getChannelTextColor,
} from 'entities/AnalyzeChannels/analyzeChannels.channelColors';

interface IComponentProps {
  // The endpoint can respond with an empty object before any data is aggregated.
  data: Partial<IAnalyzeChannelsChannelShareModel> | null;
  // Narrow layout (shift cards): thinner bars, no per-segment / cap value labels.
  compact?: boolean;
}

const HEIGHT = 250;
const MARGIN = { top: 14, right: 12, bottom: 36, left: 44 };
const PLOT_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom;
const Y_MAX = 120; // axis goes above 100% so the "100.00%" cap label has room
const Y_TICKS = [0, 20, 40, 60, 80, 100, 120];
const BAR_MAX_WIDTH = 150;
const BAR_WIDTH_RATIO = 0.94; // share of a column slot taken by the bar
const BAR_CORNER = 4; // corner radius of a stacked bar
const HOVER_STROKE = 1; // black outline width on a hovered slice
const MIN_LABEL_HEIGHT = 16; // hide a segment's % label when the slice is too thin
const TOOLTIP_GAP = 10; // px between the segment top and the tooltip

type ChannelSlice = IAnalyzeChannelsChannelShareModel['total'][number];

interface IColumn {
  label: string;
  items: ChannelSlice[];
}

interface ISegmentGeom {
  item: ChannelSlice;
  segTop: number;
  height: number;
}

interface IColumnGeom {
  column: IColumn;
  barX: number;
  centerX: number;
  totalShare: number;
  stackTop: number;
  segments: ISegmentGeom[];
}

const formatPercent = (value: number) => `${value.toFixed(2)}%`;

// Rectangle path with rounding only on the requested corners — used for the hover outline
// so the top / bottom slice follows the bar's rounded cap while inner slices stay square.
const roundedRectPath = (x: number, y: number, w: number, h: number, roundTop: boolean, roundBottom: boolean) => {
  const rt = roundTop ? Math.min(BAR_CORNER, w / 2, h / 2) : 0;
  const rb = roundBottom ? Math.min(BAR_CORNER, w / 2, h / 2) : 0;
  return [
    `M${x + rt},${y}`,
    `H${x + w - rt}`,
    rt ? `A${rt},${rt} 0 0 1 ${x + w},${y + rt}` : '',
    `V${y + h - rb}`,
    rb ? `A${rb},${rb} 0 0 1 ${x + w - rb},${y + h}` : '',
    `H${x + rb}`,
    rb ? `A${rb},${rb} 0 0 1 ${x},${y + h - rb}` : '',
    `V${y + rt}`,
    rt ? `A${rt},${rt} 0 0 1 ${x + rt},${y}` : '',
    'Z',
  ].join(' ');
};

// Keep the channels in a stable visual order regardless of the API payload order.
const orderItems = (items: ChannelSlice[]) =>
  [...items].sort((a, b) => CHANNEL_ORDER.indexOf(a.channel) - CHANNEL_ORDER.indexOf(b.channel));

export const ChannelShareChart: FC<IComponentProps> = ({ data, compact = false }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  // Unique per instance so multiple charts on one page don't share clipPath ids.
  const clipIdRef = useRef(`ac-channel-chart-clip-${Math.random().toString(36).slice(2, 9)}`);
  const [width, setWidth] = useState(720);
  const [hovered, setHovered] = useState<{ column: number; segment: number } | null>(null);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) {
      return;
    }
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) {
        setWidth(w);
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const rootClassName = `ac-channel-chart${compact ? ' ac-channel-chart--compact' : ''}`;

  const days = data?.days ?? [];
  const totalItems = data?.total ?? [];

  const columns: IColumn[] = [
    ...days.map((day) => ({ label: moment(day.date).format('ddd'), items: orderItems(day.items) })),
    ...(totalItems.length > 0 ? [{ label: 'Total', items: orderItems(totalItems) }] : []),
  ];

  if (columns.length === 0) {
    return (
      <div ref={wrapRef} className={rootClassName}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
      </div>
    );
  }

  // Narrow shift cards get a tighter left gutter and smaller axis text so the 8 column
  // labels (Sun..Sat + Total) don't collide.
  const marginLeft = compact ? 34 : MARGIN.left;
  const axisFontSize = compact ? 11 : 14;

  const plotWidth = Math.max(width - marginLeft - MARGIN.right, 0);
  const slotWidth = plotWidth / columns.length;
  const barWidth = Math.min(slotWidth * (compact ? 0.62 : BAR_WIDTH_RATIO), BAR_MAX_WIDTH);

  const yFor = (value: number) => MARGIN.top + PLOT_HEIGHT * (1 - value / Y_MAX);
  const baselineY = yFor(0);

  const columnsGeom: IColumnGeom[] = columns.map((column, columnIndex) => {
    const barX = marginLeft + slotWidth * columnIndex + (slotWidth - barWidth) / 2;
    const totalShare = column.items.reduce((sum, item) => sum + item.sharePercent, 0);

    let cumulative = 0;
    const segments = column.items.map((item) => {
      const segBottom = yFor(cumulative);
      const segTop = yFor(cumulative + item.sharePercent);
      cumulative += item.sharePercent;
      return { item, segTop, height: segBottom - segTop };
    });

    return { column, barX, centerX: barX + barWidth / 2, totalShare, stackTop: yFor(totalShare), segments };
  });

  const hoveredGeom = hovered ? columnsGeom[hovered.column] : null;
  const hoveredSegment = hovered && hoveredGeom ? hoveredGeom.segments[hovered.segment] : null;

  return (
    <div ref={wrapRef} className={rootClassName}>
      <div className="ac-channel-chart__legend">
        {CHANNEL_ORDER.map((channel) => (
          <span key={channel} className="ac-channel-chart__legend-item">
            <span className="ac-channel-chart__legend-dot" style={{ background: getChannelColor(channel) }} />
            {channel}
          </span>
        ))}
      </div>

      <div className="ac-channel-chart__plot">
        <svg width="100%" height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`} role="img">
          {/* Grid lines + Y axis labels */}
          {Y_TICKS.map((tick) => {
            const y = yFor(tick);
            return (
              <g key={tick}>
                <line
                  x1={marginLeft}
                  x2={width - MARGIN.right}
                  y1={y}
                  y2={y}
                  stroke="#dadee7"
                  strokeWidth={1}
                  strokeDasharray={tick === 0 ? undefined : '4 4'}
                />
                <text
                  x={marginLeft - 8}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="central"
                  fontSize={axisFontSize}
                  fontFamily="Poppins-Regular, Poppins, sans-serif"
                  fill="#97a2bb"
                >
                  {tick}%
                </text>
              </g>
            );
          })}

          {/* Columns */}
          {columnsGeom.map(({ column, barX, centerX, totalShare, stackTop, segments }, columnIndex) => {
            const clipId = `${clipIdRef.current}-${columnIndex}`;

            return (
              <g key={column.label}>
                {/* Rounds all four corners of the whole stacked bar */}
                <clipPath id={clipId}>
                  <rect
                    x={barX}
                    y={stackTop}
                    width={barWidth}
                    height={Math.max(baselineY - stackTop, 0)}
                    rx={BAR_CORNER}
                    ry={BAR_CORNER}
                  />
                </clipPath>

                {/* Track behind the bar */}
                <rect
                  x={barX}
                  y={yFor(100)}
                  width={barWidth}
                  height={baselineY - yFor(100)}
                  rx={BAR_CORNER}
                  fill="#edeef3"
                />

                <g clipPath={`url(#${clipId})`}>
                  {segments.map(({ item, segTop, height }, segmentIndex) => (
                    <rect
                      key={item.channel}
                      x={barX}
                      y={segTop}
                      width={barWidth}
                      height={Math.max(height, 0)}
                      fill={getChannelColor(item.channel)}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => setHovered({ column: columnIndex, segment: segmentIndex })}
                      onMouseLeave={() => setHovered(null)}
                    />
                  ))}
                </g>

                {/* Black outline on the hovered segment — uniform width, follows the bar cap.
                    Slices stack from the bottom, so index 0 is the bottom slice and the last is the top. */}
                {hovered?.column === columnIndex &&
                  hoveredSegment &&
                  (() => {
                    const isTop = hovered.segment === segments.length - 1;
                    const isBottom = hovered.segment === 0;
                    return (
                      <path
                        d={roundedRectPath(
                          barX + HOVER_STROKE / 2,
                          hoveredSegment.segTop + (isTop ? HOVER_STROKE / 2 : 0),
                          barWidth - HOVER_STROKE,
                          Math.max(
                            hoveredSegment.height - (isTop ? HOVER_STROKE / 2 : 0) - (isBottom ? HOVER_STROKE / 2 : 0),
                            0
                          ),
                          isTop,
                          isBottom
                        )}
                        fill="none"
                        stroke="#0f172b"
                        strokeWidth={HOVER_STROKE}
                        strokeLinejoin="round"
                        pointerEvents="none"
                      />
                    );
                  })()}

                {!compact &&
                  segments.map(({ item, segTop, height }) =>
                    height >= MIN_LABEL_HEIGHT ? (
                      <text
                        key={item.channel}
                        x={centerX}
                        y={segTop + height / 2}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={12}
                        fontFamily="Poppins-Regular, Poppins, sans-serif"
                        fill={getChannelTextColor(item.channel)}
                        pointerEvents="none"
                      >
                        {formatPercent(item.sharePercent)}
                      </text>
                    ) : null
                  )}

                {/* Cap label above the bar */}
                {!compact && (
                  <text
                    x={centerX}
                    y={yFor(100) - 12}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={12}
                    fontFamily="Poppins-Regular, Poppins, sans-serif"
                    fill="#97a2bb"
                  >
                    {formatPercent(totalShare)}
                  </text>
                )}

                {/* X axis label */}
                <text
                  x={centerX}
                  y={baselineY + 20}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={axisFontSize}
                  fontFamily="Poppins-Regular, Poppins, sans-serif"
                  fill="#0f172b"
                >
                  {column.label}
                </text>
              </g>
            );
          })}
        </svg>

        {hoveredGeom && hoveredSegment && (
          <div
            className="ac-channel-chart__tooltip"
            style={{ left: hoveredGeom.centerX, top: hoveredSegment.segTop - TOOLTIP_GAP }}
          >
            <div className="ac-channel-chart__tooltip-title">{hoveredGeom.column.label}</div>
            <div className="ac-channel-chart__tooltip-channel">{hoveredSegment.item.channel}</div>
            <div className="ac-channel-chart__tooltip-value">{formatPercent(hoveredSegment.item.sharePercent)}</div>
          </div>
        )}
      </div>
    </div>
  );
};
